import { LoanBookAgreementDTO, LoanBookDurationType, SettlementFeeType } from '@/models/back-office-pages/clients'
import {
  InvoiceAutomaticValidationDetailsDTO,
  InvoiceDetailsDTO,
  InvoiceReportFileDTO,
  InvoiceReportResponseDTO,
  SettlementDiscountDTOInput,
} from '@/models/back-office-pages/invoices'
import { FileFormat } from '@/models/back-office-pages/notes'
import { GroupAuthority } from '@/models/back-office-pages/system'
import {
  AdministrationTypes,
  AutoReportDTO,
  ClarificationDTO,
  Currency,
  DownloadableFileDTO,
  FromToFilterInput,
  MinMaxFilterInput,
  ReportFileDTOInput,
  ReportPeriod,
  ReportStatus,
  ReportType,
  SourcingAgentTypes,
} from '@/models/common'
import { Direction } from '@/models/table'

export enum LoanBookType {
  LOANS = 'LOANS',
  CREDIT_LINE = 'CREDIT_LINE',
  REVERSE_FACTORING = 'REVERSE_FACTORING',
}

export enum LoanBookProductStatus {
  NEW = 'init',
  APPROVING = 'approving',
  DECLINED = 'declined',
  NEEDS_CLARIFICATION = 'in_clarification',
  READY_TO_BUY = 'ready_to_buy',
  FUNDED = 'funded',
  OVERDUE = 'overdue',
  SETTLED = 'settled',
  PAID = 'paid',
  CLOSED = 'closed',
  ARCHIVED = 'archived',
}

export enum LoanBookProductStatusFilter {
  ALL = 'all',
  NEW = 'init',
  APPROVING = 'approving',
  NEEDS_CLARIFICATION = 'in_clarification',
  REJECTED = 'rejected',
  DECLINED = 'declined',
  READY_TO_BUY = 'ready_to_buy',
  FUNDED = 'funded',
  OVERDUE = 'overdue',
  CLOSED = 'closed',
  ARCHIVED = 'archived',
  PAID = 'paid',
  SETTLED = 'settled',
}

export enum LoanBookProductValidationError {
  CLIENT_CONTRACT_NOT_ACTIVE = 'CLIENT_CONTRACT_NOT_ACTIVE',
  CLIENT_NOT_APPROVED_OR_ACTIVE = 'CLIENT_NOT_APPROVED_OR_ACTIVE',
  PAYMENT_TERM_NOT_EXCEEDS_CONTRACT_END = 'PAYMENT_TERM_NOT_EXCEEDS_CONTRACT_END',
  INTEREST_AMOUNT_OVERDUE = 'INTEREST_AMOUNT_OVERDUE',
  INVOICE_NUMBER_NOT_UNIQUE = 'INVOICE_NUMBER_NOT_UNIQUE',
  PRINCIPAL_AMOUNT_OVERDUE = 'PRINCIPAL_AMOUNT_OVERDUE',
  REVERSE_FACTORING_INVOICE_AMOUNT_NOT_SUFFICIENT_FOR_PAYOUT = 'REVERSE_FACTORING_INVOICE_AMOUNT_NOT_SUFFICIENT_FOR_PAYOUT',
}

export enum PaymentScheduleStatus {
  APPROVED = 'APPROVED',
  CONSTRUCTED = 'CONSTRUCTED',
  NOT_CONSTRUCTED = 'NOT_CONSTRUCTED',
  REJECTED = 'REJECTED',
  SENT_TO_CLIENT = 'SENT_TO_CLIENT',
  UPLOADED_SIGNED = 'UPLOADED_SIGNED',
}

export interface LoanBookProductDTO {
  id: string
  agreementId: string
  productType: LoanBookType
  currency: Currency
  clientCompanyName: string
  clientId: string
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  sourcingAgentCountry: string
  status: LoanBookProductStatus
  rpaNumber: string
  productNumber: string
  invoiceNumber: string
  originalInvoiceAmount: number
  amount: number
  payoutDate: Date
  dueDate: Date
  openAmount: number
  outstandingInterest: number
  outstandingPrinciple: number
  overdueAmount: number
  daysOverdue: number
  closedAt: Date
  createdOn: Date
  approvalRequested: Date
  validationErrors: LoanBookProductValidationError[]
  paymentScheduleStatus: PaymentScheduleStatus
  supplierId: string
  supplierCompanyName: string
  paymentRecipient: PaymentRecipient
  approvalLevel: LoanBookProductApprovalLevel
  approvedCount: number
  approversTotalCount: number
  payoutNoteId: string
  payoutNoteNumber: string
}

export interface LoanBookProductTotalDTO {
  amount: number
  openAmount: number
  outstandingPrinciple: number
}

export interface PageWithTotals_LoanBookProductDTO_LoanBookProductTotalDTO {
  content: Array<LoanBookProductDTO>
  totals: LoanBookProductTotalDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookProductsData {
  loadLoanBookProductList: PageWithTotals_LoanBookProductDTO_LoanBookProductTotalDTO
}

export interface LoanBookProductsVariables {
  clientCompanyName?: string
  sourcingAgentAdministrates?: [AdministrationTypes]
  status?: Array<LoanBookProductStatus>
  rpaNumber?: string
  productNumber?: string
  amount?: MinMaxFilterInput
  payoutDate?: FromToFilterInput
  dueDate?: FromToFilterInput
  productType?: Array<LoanBookType>
  tableFilteringStatus: LoanBookProductStatusFilter
  pageSize: number
  pageNum: number
  sortDirection?: Direction
  sortField?: string
  clientId?: string
}

export interface AddNewProductsData {
  addLoanBookProduct: LoanBookProductDTO
}

export interface AddNewProductsVariables {
  gracePeriod?: number
  amount: number
  bulletPaymentPercent?: number
  bulletPaymentAmount?: number
  dueDate: string
  agreementId: string
  issueDate: string
  supplierId: string
  paymentRecipient: PaymentRecipient
}

export enum PaymentRecipient {
  CLIENT = 'CLIENT',
  SUPPLIER = 'SUPPLIER',
}

export interface AddNewProductsFormValues {
  productType: LoanBookType
  clientId: string
  amount: number
  dueDate: string
  bulletPaymentPercent: number
  bulletPaymentAmount: number
  gracePeriod: number
  agreementId: string
  agreementEndDate: string
  agreementStartDate: string
  agreementTotalAmount: number
  agreementAvailableAmount: number
  agreementDuration: number
  agreementPaymentTerm: number
  agreementDurationType: LoanBookDurationType
  issueDate: string
  bulletPaymentType: BulletPaymentType
  bulletPaymentCalculatedAmount: number
  supplierId: string
  paymentRecipient: PaymentRecipient
}

export enum ProductFieldNames {
  PRODUCT_TYPE = 'productType',
  CLIENT_ID = 'clientId',
  AGREEMENT_ID = 'agreementId',
  AGREEMENT_TOTAL_AMOUNT = 'agreementTotalAmount',
  AGREEMENT_AVAILABLE_AMOUNT = 'agreementAvailableAmount',
  AGREEMENT_ISSUE_DATE = 'agreementStartDate',
  AGREEMENT_DUE_DATE = 'agreementEndDate',
  AGREEMENT_REPAYMENT_METHOD = 'agreementDurationType',
  AGREEMENT_DURATION = 'agreementDuration',
  AGREEMENT_PAYMENT_TERM = 'agreementPaymentTerm',
  PRODUCT_AMOUNT = 'amount',
  ISSUE_DATE = 'issueDate',
  DUE_DATE = 'dueDate',
  GRACE_PERIOD = 'gracePeriod',
  BULLET_PAYMENT_TYPE = 'bulletPaymentType',
  BULLET_PAYMENT_PERCENT = 'bulletPaymentPercent',
  BULLET_PAYMENT_AMOUNT = 'bulletPaymentAmount',
  BULLET_PAYMENT_CALCULATED_AMOUNT = 'bulletPaymentCalculatedAmount',
  SUPPLIER_ID = 'supplierId',
  RECIPIENT = 'paymentRecipient',
}

export enum BulletPaymentType {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  PERCENTAGE_OF_LOAN_AMOUNT = 'PERCENTAGE_OF_LOAN_AMOUNT',
}

export enum ConditionsDocumentType {
  SCHEDULE = 'schedule',
  SPECIAL_CONDITIONS = 'special_conditions',
}

export interface LoanBookProductScheduleSummaryDTO {
  annualInterestRate: number
  bulletAmount: number
  bulletPercent: number
  loanAmount: number
  monthlyPayment: number
  totalInterest: number
  totalPayment: number
  increaseFee: number
  onboardingFee: number
  issueDate: Date
  dueDate: Date
}

export interface LoanBookConditionDocumentApproverDTO {
  approvalComment: string
  approvedAt: Date
  approvedBy: string
}

export interface LoanBookVerifierDTO {
  emails: Array<string>
  sentAt: Date
  sentBy: string
}

export interface LoanBookConditionDocumentUploaderDTO {
  signedDocumentFilename: string
  signedDocumentId: string
  signedDocumentUploadedAt: Date
  signedDocumentUploadedBy: string
  signedDocumentUploadedByClient: boolean
  signedDocumentUploadedByEmail: string
  signedDocumentUploadedComment: string
}

export interface LoanBookConditionApprovalDTO {
  approver: LoanBookConditionDocumentApproverDTO
  conditionApprovalStatus: PaymentScheduleStatus
  conditionVerifier: LoanBookVerifierDTO
  summary: LoanBookProductScheduleSummaryDTO
  uploader: LoanBookConditionDocumentUploaderDTO
}

export enum AuditActions {
  LOAN_BOOK_AGREEMENT_APPROVED = 'LOAN_BOOK_AGREEMENT_APPROVED',
  LOAN_BOOK_AGREEMENT_CLOSED = 'LOAN_BOOK_AGREEMENT_CLOSED',
  LOAN_BOOK_AGREEMENT_CREATED = 'LOAN_BOOK_AGREEMENT_CREATED',
  LOAN_BOOK_AGREEMENT_UPDATED = 'LOAN_BOOK_AGREEMENT_UPDATED',
  LOAN_BOOK_PRODUCT_ARCHIVED = 'LOAN_BOOK_PRODUCT_ARCHIVED',
  LOAN_BOOK_PRODUCT_CONDITIONS_APPROVED = 'LOAN_BOOK_PRODUCT_CONDITIONS_APPROVED',
  LOAN_BOOK_PRODUCT_ASSIGNED_SALES = 'LOAN_BOOK_PRODUCT_ASSIGNED_SALES',
  LOAN_BOOK_PRODUCT_CONDITIONS_REJECTED = 'LOAN_BOOK_PRODUCT_CONDITIONS_REJECTED',
  LOAN_BOOK_PRODUCT_CONDITIONS_SENT = 'LOAN_BOOK_PRODUCT_CONDITIONS_SENT',
  LOAN_BOOK_PRODUCT_CONDITIONS_UPLOADED = 'LOAN_BOOK_PRODUCT_CONDITIONS_UPLOADED',
  LOAN_BOOK_PRODUCT_CONDITIONS_UPLOADED_BY_CLIENT = 'LOAN_BOOK_PRODUCT_CONDITIONS_UPLOADED_BY_CLIENT',
  LOAN_BOOK_PRODUCT_CREATED = 'LOAN_BOOK_PRODUCT_CREATED',
  LOAN_BOOK_PRODUCT_DECLINED = 'LOAN_BOOK_PRODUCT_DECLINED',
  LOAN_BOOK_PRODUCT_RESET_TO_NEW = 'LOAN_BOOK_PRODUCT_RESET_TO_NEW',
  LOAN_BOOK_PRODUCT_SCHEDULE_UPDATED = 'LOAN_BOOK_PRODUCT_SCHEDULE_UPDATED',
  LOAN_BOOK_PRODUCT_UPDATED = 'LOAN_BOOK_PRODUCT_UPDATED',
}

export interface LoanBookProductLogDTO {
  createdAt: Date
  event: AuditActions
  id: string
  userFullName: string
  comment: string
  documentsToSignSentTo: Array<string>
}

export enum LoanBookProductApprovalLevel {
  EYES2 = 'EYES2',
  EYES4 = 'EYES4',
}

export interface LoanBookProductApproverDTO {
  approved: Date
  approverEmail: string
  userRole: GroupAuthority
}

export interface LoanBookProductApprovalDTO {
  approvalDeclined: Date
  approvalDeclinedBy: string
  approvalDeclinedComments: string
  approvalInitiated: Date
  approvalInitiatedBy: string
  approvalLevel: LoanBookProductApprovalLevel
  approvers: LoanBookProductApproverDTO[]
  approversCount: number
  approversTotalCount: number
  archived: Date
  archivedBy: string
  archivedComments: string
  clarificationInitiated: Date
  clarificationInitiatedBy: string
  clarificationQuestion: string
  userCanApprove: boolean
  status: LoanBookProductStatus
}

export interface LoanBookProductFundedDetailsDTO {
  outstandingInterest: number
  outstandingPrinciple: number
  payoutNoteId: string
  payoutNoteNumber: string
  repaidInterest: number
  repaidPrinciple: number
  totalInterest: number
  totalPrinciple: number
}

export enum LoanBookFullReturnStatus {
  NOT_PAID = 'NOT_PAID',
  PAID = 'PAID',
  PARTLY_PAID = 'PARTLY_PAID',
  INIT = 'INIT',
}

export interface LoanBookProductFullReturnDTO {
  fileName: string
  reportId: string
  expectedPaymentDate: string
  earlyTerminationFeeExclVat: number
  earlyTerminationFeeInclVat: number
  status: LoanBookFullReturnStatus
}
export enum LoanBookReconciliationType {
  PAYMENT_RECEIVED = 'PAYMENT_RECEIVED',
  FULL_RETURN_REPAYMENT_RECEIVED = 'FULL_RETURN_REPAYMENT_RECEIVED',
  PARTIAL_REPAYMENT_RECEIVED = 'PARTIAL_REPAYMENT_RECEIVED',
  OTHER = 'OTHER',
}

export interface LoanBookProductReconciliationDTO {
  amount: number
  date: Date
  reconciliationId: string
  reconciliationType: LoanBookReconciliationType
  increasedFees: number
  interests: number
  principal: number
}

export interface LoanBookPayoutDetailsDTO {
  accruedFees: number
  accruedIncreasedFees: number
  accruedInterests: number
  amount: number
  invoicedFees: number
  outstandingIncreasedFees: number
  outstandingInterest: number
  outstandingInvoicedFees: number
  outstandingPrincipal: number
  payoutNoteId: string
  payoutNoteNumber: string
  principal: number
  reconciliations: Array<LoanBookProductReconciliationDTO>
  repaidAmount: number
  fullReturnOpenAmount: number
  fullReturnReconciliations: Array<LoanBookProductReconciliationDTO>
  fullReturnTotalAmount: number
}

export interface LoanBookProductOverdueDetailsDTO {
  additionalFeeOverdue: number
  increasedFeeOverdue: number
  interestOverdue: number
  outstandingAdditionalFee: number
  outstandingIncreasedFee: number
  outstandingInterest: number
  outstandingPrincipal: number
  principalOverdue: number
}

export interface LoanBookProductDetailsDTO {
  approval: LoanBookProductApprovalDTO
  assigneeEmail: string
  debtCollectionAssigneeEmail: string
  automaticValidationErrors: Array<LoanBookProductValidationError>
  currency: Currency
  id: string
  productDetails: LoanBookProductDTO
  scheduleSummary: LoanBookProductScheduleSummaryDTO
  agreement: LoanBookAgreementDTO
  conditionApproval: LoanBookConditionApprovalDTO
  fundedDetails: LoanBookProductFundedDetailsDTO
  logs: Array<LoanBookProductLogDTO>
  fullReturnDetails: LoanBookProductFullReturnDTO
  vatRate: number
  debtDetails: LoanBookPayoutDetailsDTO
  overdueDetails: LoanBookProductOverdueDetailsDTO
  blockLoanBookPayments: boolean
  allAgreementSettlementsInvoiced: boolean
}

export interface ProductDetailsData {
  getLoanBookProduct: LoanBookProductDetailsDTO
}

export interface ProductDetailsVariables {
  id: string
}

export interface AssignOperationToProductData {
  assignOperationsToLoanBookProduct: InvoiceDetailsDTO
}

export interface AssignOperationToProductVariables {
  id: string
  assigneeEmail: string
}

export interface AssignOperationToProductFormValues {
  assigneeEmail: string
}

export enum AssignOperationToProductFieldsNames {
  OPERATION_EMAIL = 'assigneeEmail',
}

export interface UpdateProductDetailsFormValues extends LoanBookProductDTO {}

export enum ProductDetailsFieldsNames {
  STATUS = 'status',
  SOURCING_AGENT = 'sourcingAgentName',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  PRODUCT_TYPE = 'productType',
  CLIENT = 'clientCompanyName',
  CLIENT_ID = 'clientId',
  RPA_NUMBER = 'rpaNumber',
  PRODUCT_NUMBER = 'productNumber',
  PRODUCT_AMOUNT = 'amount',
  ISSUE_DATE = 'payoutDate',
  DUE_DATE = 'dueDate',
  SUPPLIER_ID = 'supplierId',
  RECIPIENT = 'paymentRecipient',
}

export interface UpdateProductDetailsData {
  updateLoanBookProduct: LoanBookProductDetailsDTO
}

export interface UpdateProductDetailsVariables {
  id: string
  invoiceNumber: string
  paymentRecipient: PaymentRecipient
  supplierId: string
  originalInvoiceAmount: number
}

export interface PaymentScheduleDTO {
  date: Date
  interest: number
  loanBookProductId: string
  openAmount: number
  principal: number
  remainingBalance: number
  termEnd: Date
  termStart: Date
  totalPayment: number
  vat: number
  vatNoteId: string
  vatNoteNumber: string
  status: LoanBookVatNoteStatus
}

export interface PaymentScheduleSummaryDTO {
  annualInterestRate: number
  bulletAmount: number
  bulletPercent: number
  dueDate: string
  gracePeriod: number
  issueDate: string
  loanAmount: number
  monthlyInterestRate: number
}

export interface PaymentScheduleDetailsDTO {
  schedule: PaymentScheduleDTO[]
  scheduleSummary: PaymentScheduleSummaryDTO
}

export interface PaymentScheduleDetailsData {
  loadProductSchedule: PaymentScheduleDetailsDTO
}

export interface PaymentScheduleDetailsVariables {
  id: string
}

export interface UpdatePaymentScheduleData {
  updateProductSchedule: PaymentScheduleDetailsDTO
}

export interface UpdatePaymentScheduleVariables {
  id: string
  bulletPaymentAmount: number
  bulletPaymentPercent: number
  gracePeriod: number
  issueDate: string
  dueDate: string
  amount: number
}

export interface UpdateScheduleDetailsFormValues {
  productType: LoanBookType
  agreementEndDate: string
  agreementStartDate: string
  agreementTotalAmount: number
  agreementAvailableAmount: number
  agreementDuration: number
  agreementDurationType: LoanBookDurationType
  agreementPaymentTerm: number
  amount: number
  issueDate: string
  dueDate: string
  bulletPaymentType: string
  bulletPaymentAmount: number
  bulletPaymentPercent: number
  gracePeriod: number
}

export interface DownloadPaymentScheduleData {
  downloadPaymentSchedule: ReportFileDTOInput
}

export interface DownloadPaymentScheduleVariables {
  id: string
}

export interface DownloadSpecialConditionsData {
  downloadSpecialConditions: ReportFileDTOInput
}

export interface DownloadSpecialConditionsVariables {
  id: string
}

export interface LoanBookProductCommentDTO {
  archived: boolean
  loanBookProductId: string
  comment: string
  createdBy: string
  createdByEmail: string
  createdById: string
  createdOn: Date
  flagged: boolean
  id: string
  updatedBy: string
  updatedByEmail: string
  updatedById: string
  updatedOn: Date
}

export interface GetLoanBookProductCommentsData {
  getLoanBookProductComments: Array<LoanBookProductCommentDTO>
}

export interface GetLoanBookProductCommentsVariables {
  id: string
}

export interface GetLoanBookProductCommentByIdData {
  getLoanBookProductCommentById: LoanBookProductCommentDTO
}

export interface GetLoanBookProductCommentsByIdVariables {
  commentId: string
}

export interface AddLoanBookProductCommentData {
  addLoanBookProductComment: LoanBookProductCommentDTO
}

export interface AddLoanBookProductCommentVariables {
  id: string
  comment: string
}

export interface UpdateLoanBookProductCommentData {
  updateLoanBookProductComment: LoanBookProductCommentDTO
}

export interface UpdateLoanBookProductCommentVariables {
  comment: string
  commentId: string
}

export interface MarkLoanBookProductCommentAsFlaggedData {
  markLoanBookProductCommentAsFlagged: LoanBookProductCommentDTO
}

export interface MarkLoanBookProductCommentAsFlaggedVariables {
  commentId: string
}

export interface MarkLoanBookProductCommentAsUnflaggedData {
  markLoanBookProductCommentAsUnflagged: LoanBookProductCommentDTO
}

export interface MarkLoanBookProductCommentAsUnflaggedVariables {
  commentId: string
}

export interface MarkLoanBookProductCommentAsArchivedData {
  markLoanBookProductCommentAsArchived: LoanBookProductCommentDTO
}

export interface MarkLoanBookProductCommentAsArchivedVariables {
  commentId: string
}

export interface MarkLoanBookProductCommentAsUnarchivedData {
  markLoanBookProductCommentAsUnarchived: LoanBookProductCommentDTO
}

export interface MarkLoanBookProductCommentAsUnarchivedVariables {
  commentId: string
}

export interface DownloadLoanBookProductDocumentData {
  downloadLoanBookProductDocument: ReportFileDTOInput
}

export interface DownloadLoanBookProductDocumentVariables {
  documentId: string
}

export interface UploadLoanBookProductDocumentData {
  uploadLoanBookProductDocument: string
}

export enum LoanBookProductDocumentType {
  CONTRACT = 'CONTRACT',
  INVOICE = 'INVOICE',
  SIGNED_SCHEDULE = 'SIGNED_SCHEDULE',
  SIGNED_SPECIAL_CONDITIONS = 'SIGNED_SPECIAL_CONDITIONS',
  INTERNAL = 'INTERNAL',
  OTHER = 'OTHER',
}

export interface UploadLoanBookProductDocumentVariables {
  reportType: LoanBookProductDocumentType
  productId?: string
  file: ReportFileDTOInput
  reportCreatedAt: string
  description: string
}

export interface UploadLoanBookProductDocumentFormValues {
  reportType: LoanBookProductDocumentType
  productId?: string
  files: ReportFileDTOInput[]
  reportCreatedAt: string
  description: string
}

export interface DeleteLoanBookProductDocumentData {
  deleteLoanBookProductDocument: LoanBookProductDocumentDTO
}

export interface DeleteLoanBookProductDocumentVariables {
  documentId: string
}

export interface LoanBookProductDocumentDTO {
  productId: string
  content: string
  description: string
  documentId: string
  fileName: string
  reportCreatedAt: string
  reportType: LoanBookProductDocumentType
  uploader: string
}

export interface Page_LoanBookProductDocumentDTO {
  content: InvoiceReportFileDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookProductDocumentsData {
  loanBookProductDocumentList: Page_LoanBookProductDocumentDTO
}

export interface LoanBookProductDocumentsVariables {
  productId: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface SendLoanBookDocumentForSigningForm {
  clientEmails: Array<string>
}

export enum SendLoanBookDocumentForSigningFieldNames {
  EMAILS = 'clientEmails',
}

export interface SendLoanBookDocumentForSigningVariables {
  id: string
  clientEmails: Array<string>
}

export interface SendLoanBookDocumentForSigningData {
  sendLoanBookDocumentForSigning: LoanBookProductDetailsDTO
}

export interface UploadLoanBookProductSignedDocumentFormData {
  signedDocument: ReportFileDTOInput
  comment: string
}

export enum UploadLoanBookProductSignedDocumentFieldsNames {
  FILE = 'signedDocument',
  COMMENT = 'comment',
}

export interface UploadLoanBookProductSignedDocumentVariables {
  id: string
  signedDocument: ReportFileDTOInput
  comment: string
}

export interface UploadLoanBookProductSignedDocumentData {
  uploadLoanBookProductSignedDocument: LoanBookProductDetailsDTO
}

export interface LoanProductConditionsFormData {
  comment: string
}

export enum SADecisionFieldsNames {
  COMMENT = 'comment',
}

export interface ApproveLoanProductConditionsVariables {
  id: string
  comment: string
}

export interface ApproveLoanProductConditionsData {
  approveLoanProductConditions: InvoiceAutomaticValidationDetailsDTO
}

export interface RejectLoanProductConditionsVariables {
  id: string
  comment: string
}

export interface RejectLoanProductConditionsData {
  rejectLoanProductConditions: InvoiceAutomaticValidationDetailsDTO
}

export enum CommentType {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
  NO_COMMENT = 'NO_COMMENT',
}

export enum ProductActionFieldsSchema {
  IDS = 'ids',
  COMMENT = 'comment',
  COMMENT_TYPE = 'commentType',
}

export interface ProductActionFormProps {
  ids: Array<string>
  comment: string
  commentType: CommentType
}

export interface ArchiveProductData {
  archiveLoanProduct: LoanBookProductDetailsDTO
}

export interface ArchiveProductVariables {
  id: string
  comment: string
}

export interface ResetToNewLoanProductData {
  resetToNewLoanProduct: LoanBookProductDetailsDTO
}

export interface ResetToNewLoanProductVariables {
  id: string
}

export interface DeclineLoanProductData {
  declineLoanProduct: LoanBookProductDetailsDTO
}

export interface DeclineLoanProductVariables {
  id: string
  comment: string
}

export interface CloseLoanProductData {
  closeLoanProduct: LoanBookProductDetailsDTO
}

export interface CloseLoanProductVariables {
  id: string
}

export interface ApproveLoanProductData {
  approveLoanProduct: LoanBookProductDetailsDTO
}

export interface ApproveLoanProductVariables {
  id: string
  comment: string
}

export interface InitiateLoanProductApprovalData {
  initiateLoanProductApproval: LoanBookProductDetailsDTO
}

export interface InitiateLoanProductApprovalVariables {
  id: string
}

export interface StartClarificationLoanBookProductData {
  startClarificationLoanBookProduct: LoanBookProductDetailsDTO
}

export interface StartClarificationLoanBookProductVariables {
  id: string
  comment: string
}

export interface FinishClarificationLoanBookProductData {
  finishClarificationLoanBookProduct: LoanBookProductDetailsDTO
}

export interface FinishClarificationLoanBookProductVariables {
  id: string
}

export interface PayoutLoanBookProductData {
  payoutLoanBookProduct: LoanBookProductDetailsDTO
}

export interface PayoutLoanBookProductVariables {
  productId: string
  settlementDiscounts: SettlementDiscountDTOInput[]
}

export enum PayoutLoanBookProductFormFieldArrayNames {
  AMOUNT = 'amount',
  DISCOUNT_AMOUNT = 'discountAmount',
  DISCOUNT_AMOUNT_WITH_VAT = 'discountAmountWithVat',
  REMAINING_AMOUNT = 'remainingAmount',
}

export enum PayoutLoanBookProductFormFieldsNames {
  SETTLEMENT_DISCOUNTS = 'settlementDiscounts',
}

export interface LoanBookClarificationCommentDTO {
  comment: string
  createdOn: Date
  fullName: string
  usersLogin: string
}

export interface LoanBookClarificationDTO {
  closed: boolean
  comment: string
  createdOn: Date
  explanations: LoanBookClarificationCommentDTO[]
  fullName: string
  usersLogin: string
}

export interface AddLoanBookProductClarificationCommentData {
  addLoanBookProductClarificationComment: LoanBookClarificationDTO[]
}

export interface AddLoanBookProductClarificationCommentVariables {
  productId: string
  comment: string
}

export interface LoanBookProductClarificationsData {
  loanBookProductClarifications: ClarificationDTO[]
}

export interface LoanBookProductClarificationsVariables {
  productId: string
}

export enum PurchaseNoteStatus {
  BLOCKED = 'blocked',
  FAILED = 'failed',
  PAID = 'paid',
  PROCESSING = 'processing',
  QUEUED = 'queued',
}

export interface LoanBookPayoutNoteDTO {
  clientId: string
  companyName: string
  currency: Currency
  id: string
  issueDate: Date
  payoutNoteNumber: string
  productNumber: string
  productType: LoanBookType
  sourcingAgentAdministrates: string
  sourcingAgentCountry: string
  sourcingAgentId: string
  sourcingAgentName: string
  status: PurchaseNoteStatus
  totalPayable: number
  totalProductAmount: number
}

export interface LoanBookPayoutNoteTotalsDTO {
  totalPayable: number
  totalProductAmount: number
}

export interface PageWithTotals_LoanBookPayoutNoteDTO_LoanBookPayoutNoteTotalsDTO {
  content: Array<LoanBookPayoutNoteDTO>
  totals: LoanBookPayoutNoteTotalsDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookPayoutNoteData {
  loanBookPayoutNoteList: PageWithTotals_LoanBookPayoutNoteDTO_LoanBookPayoutNoteTotalsDTO
}

export interface LoanBookPayoutNoteVariables {
  noteNumber?: string
  status?: PurchaseNoteStatus[]
  issueDate?: FromToFilterInput
  currency?: Currency[]
  companyName?: string
  sourcingAgentAdministrates?: AdministrationTypes[]
  productNumber?: string
  productType?: LoanBookType[]
  totalProductAmount?: MinMaxFilterInput
  totalPayable?: MinMaxFilterInput
  sortDirection: Direction
  sortField: string
  pageSize: number
  pageNum: number
}

export interface DownloadLoanBookPayoutNoteData {
  downloadLoanBookPayoutNote: DownloadableFileDTO
}

export interface DownloadLoanBookPayoutNoteVariables {
  id: string
}

export interface BlockPayoutNoteData {
  blockLoanBookPayoutNote: boolean
}

export interface BlockPayoutNoteVariables {
  id: string
}

export interface UnBlockPayoutNoteData {
  unblockLoanBookPayoutNote: boolean
}

export interface UnBlockPayoutNoteVariables {
  id: string
}

export interface MarkPayoutNoteAsPaidData {
  markLoanBookPayoutNoteAsPaid: boolean
}

export interface MarkPayoutNoteAsPaidVariables {
  id: string
}

export interface LoanBookFullReturnDTO {
  fullReturnNoteNumber: string
  clientId: string
  clientCompanyName: string
  currency: Currency
  id: string
  issueDate: Date
  expectedPaymentDate: Date
  agreementId: string
  rpaNumber: string
  productNumber: string
  productId: string
  status: LoanBookFullReturnStatus
  totalAmount: number
  openAmount: number
  productType: LoanBookType
}

export interface LoanBookFullReturnNoteTotalsDTO {
  totalAmount: number
  openAmount: number
}

export interface PageWithTotals_LoanBookFullReturnNoteDTO_LoanBookFullReturnNoteTotalsDTO {
  content: Array<LoanBookFullReturnDTO>
  totals: LoanBookFullReturnNoteTotalsDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookFullReturnNoteData {
  loanBookFullReturnNoteList: PageWithTotals_LoanBookFullReturnNoteDTO_LoanBookFullReturnNoteTotalsDTO
}

export interface LoanBookFullReturnNoteVariables {
  noteNumber?: string
  rpaNumber?: string
  status?: PurchaseNoteStatus[]
  issueDate?: FromToFilterInput
  expectedPaymentDate?: FromToFilterInput
  currency?: Currency[]
  companyName?: string
  productNumber?: string
  totalAmount?: MinMaxFilterInput
  openAmount?: MinMaxFilterInput
  sortDirection: Direction
  sortField: string
  pageSize: number
  pageNum: number
}

export interface DownloadLoanBookFullReturnNoteData {
  downloadLoanBookFullReturnNote: DownloadableFileDTO
}

export interface DownloadLoanBookFullReturnNoteVariables {
  id: string
}

export interface ResendLoanBookFullReturnNoteToClientData {
  resendLoanBookFullReturnNoteToClient: boolean
}

export interface ResendLoanBookFullReturnNoteToClientVariables {
  id: string
}

export interface LoanBookSettlementForDiscountDTO {
  amount: number
  amountInclVat: number
  createdOn: Date
  currency: Currency
  description: string
  feeType: SettlementFeeType
  id: string
  vat: number
}

export interface LoanBookSettlementForDiscountFormData extends LoanBookSettlementForDiscountDTO {
  discountAmount: number
  discountAmountWithVat: number
  remainingAmount: number
}

export interface LoanBookProductSettlementsForDiscountsData {
  loanBookProductSettlementsForDiscounts: Array<LoanBookSettlementForDiscountDTO>
}

export interface LoanBookProductSettlementsForDiscountsVariables {
  clientId: string
}

export enum LoanBookVatNoteStatus {
  NOT_PAID = 'NOT_PAID',
  PAID = 'PAID',
  PARTLY_PAID = 'PARTLY_PAID',
  SETTLED = 'SETTLED',
}

export enum LoanBookVatNoteInvoiceType {
  ADDITIONAL_FEES = 'ADDITIONAL_FEES',
  MONTHLY = 'MONTHLY',
}

export interface LoanBookVatNoteDTO {
  currency: Currency
  clientId: string
  companyName: string
  id: string
  issueDate: Date
  outstandingAmount: number
  outstandingIncreasedFee: number
  outstandingInterest: number
  outstandingPrincipal: number
  productType: LoanBookType
  invoiceType: LoanBookVatNoteInvoiceType
  agreementId: string
  rpaNumber: string
  sourcingAgentAdministrates: string
  sourcingAgentCountry: string
  sourcingAgentId: string
  sourcingAgentName: string
  status: LoanBookVatNoteStatus
  vatNoteNumber: string
  dueDate: Date
  payableInclVat: number
  daysOverdue: number
  totalInterestInclVat: number
  totalIncreasedFeeInclVat: number
}

export interface LoanBookVatNoteTotalsDTO {
  totalOutstandingIncreasedFee: number
  totalOutstandingInterest: number
  totalOutstandingPrincipal: number
  totalPayableInclVat: number
  totalOutstandingAmount: number
  totalIncreasedFeeInclVat: number
  totalInterestInclVat: number
}

export interface PageWithTotals_LoanBookVatNoteDTO_LoanBookVatNoteTotalsDTO {
  content: Array<LoanBookVatNoteDTO>
  totals: LoanBookVatNoteTotalsDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookVatNoteData {
  loanBookVatNoteList: PageWithTotals_LoanBookVatNoteDTO_LoanBookVatNoteTotalsDTO
}

export interface LoanBookVatNoteVariables {
  vatNoteNumber?: string
  issueDate?: FromToFilterInput
  companyName?: string
  sourcingAgentAdministrates?: AdministrationTypes[]
  invoiceType?: LoanBookVatNoteInvoiceType[]
  status?: LoanBookVatNoteStatus[]
  rpaNumber?: string
  productType?: LoanBookType[]
  invoiceAmount?: MinMaxFilterInput
  outstandingAmount?: MinMaxFilterInput
  outstandingIncreasedFee?: MinMaxFilterInput
  outstandingInterest?: MinMaxFilterInput
  outstandingPrincipal?: MinMaxFilterInput
  totalInterestInclVat?: MinMaxFilterInput
  totalIncreasedFeeInclVat?: MinMaxFilterInput
  dueDate?: FromToFilterInput
  daysOverdue?: MinMaxFilterInput
  pageSize: number
  pageNum: number
  sortField: string
  sortDirection: Direction
  clientId?: string
}

export interface DownloadLoanBookVatNoteData {
  downloadLoanBookVatNote: DownloadableFileDTO
}

export interface DownloadLoanBookVatNoteVariables {
  id: string
  fileFormat?: FileFormat
}

export interface VatNoteGenerateResultDTO {
  generatedNotes: number
}

export interface GenerateLoanBookVatNotesData {
  generateLoanBookVatNotes: VatNoteGenerateResultDTO
}

export interface GenerateLoanBookVatNotesVariables {
  period: string
  sourcingAgentAdministrates: AdministrationTypes
  clientIds: Array<string>
  productType: LoanBookType
  sendEmails: boolean
}

export interface GenerateLoanBookVatNotesFormValues {
  period: string
  clientIds: Array<string>
  productType: LoanBookType
  sendEmails: boolean
}

export interface GenerateLoanBookSettlementNotesData {
  generateSettlementLoanBookVatNotes: VatNoteGenerateResultDTO
}

export enum LoanBookSettlementFeeType {
  ADMINISTRATION_FEE = 'ADMINISTRATION_FEE',
  COMPENSATION_INDEMNITY_FEE = 'COMPENSATION_INDEMNITY_FEE',
  LIMIT_INCREASE_FEE = 'LIMIT_INCREASE_FEE',
  ONBOARDING_FEE = 'ONBOARDING_FEE',
}

export type ExtendedLoanBookSettlementFeeType = LoanBookSettlementFeeType | 'ALL'

export interface GenerateLoanBookSettlementNotesVariables {
  period: string
  sourcingAgentAdministrates: AdministrationTypes
  clientIds: Array<string>
  feeType: LoanBookSettlementFeeType
  sendEmails: boolean
}

export interface GenerateLoanBookSettlementNotesFormValues {
  period: string
  clientIds: Array<string>
  feeType: ExtendedLoanBookSettlementFeeType
  sendEmails: boolean
}

export enum GenerateLoanBookVatNotesFieldsNames {
  MONTH_PERIOD = 'period',
  SETTLEMENT_TYPE = 'feeType',
  CLIENTS_IDS = 'clientIds',
  PRODUCT_TYPE = 'productType',
  NOTIFY_CLIENTS = 'sendEmails',
}

export enum ReGenerateLoanBookVatNotesFieldsNames {
  NOTIFY_CLIENTS = 'sendEmails',
}

export interface RegenerateLoanBookVatNotesData {
  recreateVatNotes: VatNoteGenerateResultDTO
}

export interface RegenerateLoanBookVatNotesVariables {
  vatNoteIds: Array<string>
  sendEmails: boolean
}

export interface ReGenerateLoanBookVatNotesFormValues {
  sendEmails: boolean
}

export enum IssueFullReturnFieldsSchema {
  PAYMENT_DATE = 'expectedPaymentDate',
  EARLY_TERMINATION_FEE = 'earlyTerminationFeeExclVat',
  EARLY_TERMINATION_FEE_INCL_VAT = 'earlyTerminationFeeInclVat',
  VAT = 'vat',
  SEND_REPORT = 'sendEmails',
  SETTLE_AMOUNTS = 'settleAmounts',
}

export interface IssueFullReturnFormValues {
  expectedPaymentDate: string
  earlyTerminationFeeExclVat: number
  earlyTerminationFeeInclVat: number
  vat: number
  sendEmails: boolean
  settleAmounts: boolean
}

export interface IssueFullReturnData {
  generateLoanBookFullReturn: boolean
}

export interface IssueFullReturnVariables {
  productId: string
  sendEmails: boolean
  expectedPaymentDate: string
  earlyTerminationFeeExclVat: number
  settleAmounts: boolean
}

export interface CancelLoanBookFullReturnData {
  cancelLoanBookFullReturn: boolean
}

export interface CancelLoanBookFullReturnVariables {
  productId: string
}

export interface LoanBookReportDTO {
  createdOn: Date
  documentId: string
  fileName: string
  from: Date
  reportPeriod: ReportPeriod
  sourcingAgentAdministrates: AdministrationTypes
  to: Date
  reportType: LoanBookReportType
  status: ReportStatus
  uploader: string
  content: string
}

export interface Page_LoanBookReportDTO {
  content: LoanBookReportDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookReportsData {
  listLoanBookReports: Page_LoanBookReportDTO
}

export interface LoanBookReportsVariables {
  createdOn?: FromToFilterInput
  sourcingAgentAdministrates?: AdministrationTypes[]
  reportType?: ReportType[]
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface DownloadLoanBookReportData {
  downloadLoanBookReport: AutoReportDTO
}

export interface DownloadLoanBookReportVariables {
  reportId: string
  reportType: LoanBookReportType
}

export interface CancelLoanBookReportData {
  cancelLoanBookReport: InvoiceReportResponseDTO
}

export interface CancelLoanBookReportVariables {
  reportId: string
  reportType: LoanBookReportType
}

export enum LoanBookReportType {
  LOAN_BOOK_PAYOUT_NOTE = 'LOAN_BOOK_PAYOUT_NOTE',
  LOAN_BOOK_VAT_NOTE = 'LOAN_BOOK_VAT_NOTE',
  LOAN_BOOK_RECONCILIATION = 'LOAN_BOOK_RECONCILIATION',
  LOAN_BOOK_PORTFOLIO = 'LOAN_BOOK_PORTFOLIO',
  LOAN_BOOK_AGC = 'LOAN_BOOK_AGC',
}
export interface GenerateLoanBookPayoutNoteReportData {
  requestLoanBookPayoutNoteReport: string
}

export interface GenerateLoanBookPayoutNoteReportVariables {
  reportPeriod: FromToFilterInput
  reportType: LoanBookReportType
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface GenerateLoanBookVatNoteReportData {
  requestLoanBookVatNoteReport: string
}

export interface GenerateLoanBookReconciliationReportVariables {
  reportPeriod: FromToFilterInput
  reportType: LoanBookReportType
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface GenerateLoanBookReconciliationReportData {
  requestLoanBookReconciliationReport: string
}

export interface GenerateLoanBookVatNoteReportVariables {
  reportPeriod: string
  reportType: LoanBookReportType
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface GenerateLoanBookPortfolioReportData {
  requestLoanBookPortfolioReport: string
}

export interface GenerateLoanBookPortfolioReportVariables {
  reportPeriod: string
  reportType: LoanBookReportType
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface GenerateLoanBookAgcReportData {
  requestLoanBookAgcReport: string
}

export interface GenerateLoanBookAgcReportVariables {
  reportPeriod: string
  reportType: LoanBookReportType
  sourcingAgentAdministrates: SourcingAgentTypes
}

export interface GenerateLoanBookReportFormValues {
  reportPeriod: string | FromToFilterInput
  reportType: LoanBookReportType
  sourcingAgentAdministrates: SourcingAgentTypes
}

export enum GenerateLoanBookReportFieldsNames {
  PERIOD = 'reportPeriod',
  REPORT_TYPE = 'reportType',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
}

export interface AddReverseFactoringInvoiceData {
  addReverseFactoringInvoice: boolean
}

export interface AddReverseFactoringInvoiceFormValues {
  originalInvoiceAmount: number
  invoiceFile: ReportFileDTOInput
  supplierId: string
  dueDate: string
  invoiceNumber: string
  issueDate: string
}

export interface AddReverseFactoringInvoiceVariables extends AddReverseFactoringInvoiceFormValues {
  productId: string
}

export enum ReverseFactoringInvoiceFieldsNames {
  SUPPLIER_ID = 'supplierId',
  INVOICE_NUMBER = 'invoiceNumber',
  ISSUE_DATE = 'issueDate',
  DUE_DATE = 'dueDate',
  INVOICE_AMOUNT = 'originalInvoiceAmount',
  INVOICE_FILE = 'invoiceFile',
}

export interface UpdateReverseFactoringInvoiceData {
  updateReverseFactoringInvoice: boolean
}

export interface UpdateReverseFactoringInvoiceVariables {
  originalInvoiceAmount: number
  supplierId: string
  dueDate: string
  invoiceNumber: string
  issueDate: string
  id: string
}

export enum UpdateReverseFactoringInvoiceFieldsNames {
  SUPPLIER_ID = 'supplierId',
  INVOICE_AMOUNT = 'originalInvoiceAmount',
  INVOICE_NUMBER = 'invoiceNumber',
  ISSUE_DATE = 'issueDate',
  DUE_DATE = 'dueDate',
}

export interface LoanBookReverseFactoringInvoiceDTO {
  dueDate: string
  id: string
  invoiceNumber: string
  issueDate: string
  originalInvoiceAmount: number
  supplierCompanyName: string
  supplierId: string
}

export interface Page_LoanBookReverseFactoringInvoiceDTO {
  content: LoanBookReverseFactoringInvoiceDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ReverseFactoringInvoicesData {
  filterReverseFactoringInvoices: Page_LoanBookReverseFactoringInvoiceDTO
}

export interface ReverseFactoringInvoicesVariables {
  originalInvoiceAmount?: MinMaxFilterInput
  sortDirection?: Direction
  productId: string
  dueDate?: FromToFilterInput
  sortField?: string
  invoiceNumber?: string
  pageSize: number
  supplierCompanyName?: string
  issueDate?: FromToFilterInput
  pageNum: number
}

export interface DeleteReverseFactoringInvoiceData {
  deleteReverseFactoringInvoice: boolean
}

export interface DeleteReverseFactoringInvoiceVariables {
  invoiceId: string
}

export enum LoanBookDCTaskAction {
  BLOCK_FINANCING = 'BLOCK_FINANCING',
  COLLATERAL_ENFORCEMENT = 'COLLATERAL_ENFORCEMENT',
  CALL_CLIENT = 'CALL_CLIENT',
  SEND_NOTICE_TO_CLIENT = 'SEND_NOTICE_TO_CLIENT',
  REMINDER_I = 'REMINDER_I',
  REMINDER_II = 'REMINDER_II',
  REMINDER_III = 'REMINDER_III',
  REVIEW = 'REVIEW',
  REVIEW_SECURITY_MEASURE = 'REVIEW_SECURITY_MEASURE',
  OTHER = 'OTHER',
}

export interface LoanBookDCOverdueTaskDTO {
  clientId: string
  companyName: string
  creditRiskManager: string
  daysOverdue: number
  dueDate: Date
  outstandingAmount: number
  overdueItems: number
  payoutAmount: number
  sourcingAgentAdministrates: string
  taskAction: LoanBookDCTaskAction
}

export interface LoanBookDCOverdueTaskTotalsDTO {
  totalOutstandingAmount: number
  totalPayoutAmount: number
}

export interface PageWithTotals_LoanBookDCOverdueTaskDTO_LoanBookDCOverdueTaskTotalsDTO {
  content: Array<LoanBookDCOverdueTaskDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
  totals: LoanBookDCOverdueTaskTotalsDTO
}

export interface LoanBookDebtCollectionData {
  loanBookCreditManagementTasks: PageWithTotals_LoanBookDCOverdueTaskDTO_LoanBookDCOverdueTaskTotalsDTO
}

export enum LoanBookTaskFilter {
  ALL = 'ALL',
  ASSIGNED_TO_ME = 'ASSIGNED_TO_ME',
  OPEN = 'OPEN',
  UNASSIGNED = 'UNASSIGNED',
}

export interface LoanBookDebtCollectionVariables {
  taskAction?: LoanBookDCTaskAction[]
  payoutAmount?: MinMaxFilterInput
  outstandingAmount?: MinMaxFilterInput
  overdueItems?: MinMaxFilterInput
  companyName?: string
  administrationTypes?: AdministrationTypes[]
  daysOverdue?: MinMaxFilterInput
  dueDate?: FromToFilterInput
  pageSize?: number
  pageNum?: number
  taskFilter: LoanBookTaskFilter
  currency?: Array<Currency>
  sortDirection?: Direction
  sortField?: string
}

export interface LoanBookTaskPaymentsDTO {
  id: string
  missedPayment: number
  issueDate: Date
  daysOverdue: number
  paymentType: string
  rpaNumber: string
  paymentAmount: number
  overdueAmount: number
  outstandingPrincipal: number
  outstandingInterest: number
  outstandingIncreasedFee: number
  calculatedIncreasedFee: number
  status: string
}

export enum LoanBookDCTaskStatus {
  DONE = 'DONE',
  OPEN = 'OPEN',
}

export interface LoanBookDCOverdueActionDTO {
  agreementId: string
  agreementRPANumber: string
  closedAt: Date
  closedBy: string
  dueDate: string
  id: string
  productType: LoanBookType
  productId: string
  status: LoanBookDCTaskStatus
  taskAction: LoanBookDCTaskAction
  taskInformation: string
  vatNoteId: string
  vatNoteNumber: string
  bulletPayment: boolean
  bulletProductNumber: string
  securityMeasureDocumentId: string
  securityMeasureDocumentName: string
  securityMeasureId: string
}

export enum LoanBookDCActionType {
  TRIGGERED_ACTIONS = 'TRIGGERED_ACTIONS',
  PENDING_ACTIONS = 'PENDING_ACTIONS',
  ACTION_HISTORY = 'ACTION_HISTORY',
}

export enum LoanBookDCActionFilter {
  CLOSED = 'CLOSED',
  PENDING = 'PENDING',
  TRIGGERED = 'TRIGGERED',
}

export interface Page_LoanBookDCOverdueActionDTO {
  content: Array<LoanBookDCOverdueActionDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookCreditManagementActionsData {
  loanBookCreditManagementActions: Page_LoanBookDCOverdueActionDTO
}

export interface LoanBookCreditManagementActionsVariables {
  taskAction?: Array<LoanBookDCTaskAction>
  actionType: LoanBookDCActionFilter
  sortDirection?: Direction
  clientId: string
  dueDate?: FromToFilterInput
  sortField?: string
  pageSize: number
  vatNoteNumber?: string
  vatNoteId?: string
  pageNum: number
  productType?: Array<LoanBookType>
}

export interface RescheduleLoanBookTaskData {
  rescheduleLoanBookCreditManagementTask: boolean
}

export interface RescheduleLoanBookTaskVariables {
  taskInformation: string
  dueDate: string
  taskIds: Array<string>
}

export interface RescheduleLoanBookTaskFormValues {
  taskInformation: string
  dueDate: string
}

export enum RescheduleLoanBookTaskFieldNames {
  DUE_DATE = 'dueDate',
  INFORMATION = 'taskInformation',
  TASK_ID = 'taskId',
}

export interface MarkTaskAsDoneLoanBookTaskData {
  closeLoanBookCreditManagementTask: boolean
}

export interface MarkTaskAsDoneLoanBookTaskVariables {
  taskIds: Array<string>
}

export interface CreateLoanBookTaskData {
  createAdditionalLoanBookCreditManagementTask: boolean
}

export interface CreateLoanBookTaskVariables {
  taskAction: LoanBookDCTaskAction
  taskInformation?: string
  dueDate: string
  agreementId: string
  vatNoteId?: string
  productId?: string
  bulletPayment: boolean
}

export enum MissedPaymentType {
  VAT_NOTE = 'VAT_NOTE',
  BULLET_PAYMENT = 'BULLET_PAYMENT',
}

export interface CreateLoanBookTaskFormValues {
  taskAction: LoanBookDCTaskAction
  taskInformation?: string
  dueDate: string
  agreementId: string
  missedPaymentType: MissedPaymentType
  vatNoteId?: string
  productId?: string
}

export enum CreateLoanBookTaskFieldNames {
  ACTION = 'taskAction',
  INFORMATION = 'taskInformation',
  DUE_DATE = 'dueDate',
  AGREEMENT_ID = 'agreementId',
  VAT_NOTE_ID = 'vatNoteId',
  MISSED_PAYMENT_TYPE = 'missedPaymentType',
  BULLET_PAYMENT_ID = 'productId',
}

export interface LoanBookAgreementsVatNotesData {
  loanBookAgreementsVatNoteList: Array<LoanBookVatNoteDTO>
}

export interface LoanBookAgreementsVatNotesVariables {
  agreementId: string
  noteNumber?: string
}

export interface LoanBookDCBulletPaymentProductDTO {
  productId: string
  productNumber?: string
}

export interface LoanBookAgreementsBulletPaymentProductsData {
  loanBookAgreementsBulletPaymentProductList: Array<LoanBookDCBulletPaymentProductDTO>
}

export interface LoanBookAgreementsBulletPaymentProductsVariables {
  agreementId: string
  productNumber?: string
}

export interface LoanBookTaskCommentListDTO {
  archived: boolean
  clientId: string
  comment: string
  createdBy: string
  createdByEmail: string
  createdById: string
  createdOn: Date
  flagged: boolean
  id: string
  updatedBy: string
  updatedByEmail: string
  updatedById: string
  updatedOn: Date
}

export interface LoanBookTaskCommentDTO {
  archived: boolean
  clientId: string
  comment: string
  createdBy: string
  createdByEmail: string
  createdById: string
  createdOn: Date
  flagged: boolean
  id: string
  updatedBy: string
  updatedByEmail: string
  updatedById: string
  updatedOn: Date
}

export interface GetLoanBookTaskCommentsData {
  getLoanBookTaskComments: Array<LoanBookTaskCommentListDTO>
}

export interface GetLoanBookTaskCommentsVariables {
  id: string
}

export interface GetLoanBookTaskCommentByIdData {
  getLoanBookTaskCommentById: LoanBookTaskCommentDTO
}

export interface GetLoanBookTaskCommentsByIdVariables {
  commentId: string
}

export interface AddLoanBookTaskCommentData {
  addLoanBookTaskComment: LoanBookTaskCommentDTO
}

export interface AddLoanBookTaskCommentVariables {
  id: string
  comment: string
}

export interface UpdateLoanBookTaskCommentData {
  updateLoanBookTaskComment: LoanBookTaskCommentDTO
}

export interface UpdateLoanBookTaskCommentVariables {
  comment: string
  commentId: string
}

export interface MarkLoanBookTaskCommentAsFlaggedData {
  markLoanBookTaskCommentAsFlagged: LoanBookTaskCommentDTO
}

export interface MarkLoanBookTaskCommentAsFlaggedVariables {
  commentId: string
}

export interface MarkLoanBookTaskCommentAsUnflaggedData {
  markLoanBookTaskCommentAsUnflagged: LoanBookTaskCommentDTO
}

export interface MarkLoanBookTaskCommentAsUnflaggedVariables {
  commentId: string
}

export interface MarkLoanBookTaskCommentAsArchivedData {
  markLoanBookTaskCommentAsArchived: LoanBookTaskCommentDTO
}

export interface MarkLoanBookTaskCommentAsArchivedVariables {
  commentId: string
}

export interface MarkLoanBookTaskCommentAsUnarchivedData {
  markLoanBookTaskCommentAsUnarchived: LoanBookTaskCommentDTO
}

export interface MarkLoanBookTaskCommentAsUnarchivedVariables {
  commentId: string
}

export enum LoanBookSecurityMeasureFieldNames {
  SECURITY_MEASURE_TYPE = 'securityMeasureType',
  SECURITY_MEASURE = 'securityMeasure',
  COMPANY_NAME = 'companyName',
  COMPANY_REGISTRATION_NUMBER = 'companyCode',
  NATURAL_PERSON_NAME = 'naturalPersonName',
  NATURAL_PERSON_SURNAME = 'naturalPersonSurname',
  AMOUNT_TYPE = 'amountType',
  AMOUNT = 'amount',
  COLLATERAL = 'collateral',
  LVT = 'ltvRatio',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  SECONDARY_PLEDGE = 'secondaryPledge',
  DOCUMENT = 'document',
  COMMENT = 'comment',
}

export enum LoanBookSecurityMeasureOption {
  BILL_OF_EXCHANGE = 'BILL_OF_EXCHANGE',
  COMMERCIAL_REAL_ESTATE = 'COMMERCIAL_REAL_ESTATE',
  COVENANTS = 'COVENANTS',
  FINANCIAL_ASSETS_PLEDGE = 'FINANCIAL_ASSETS_PLEDGE',
  GUARANTEE = 'GUARANTEE',
  INDUSTRIAL_MACHINERY_AND_EQUIPMENT = 'INDUSTRIAL_MACHINERY_AND_EQUIPMENT',
  INSURANCE = 'INSURANCE',
  INTANGIBLE_ASSETS_PLEDGE = 'INTANGIBLE_ASSETS_PLEDGE',
  LAND = 'LAND',
  RECEIVABLES = 'RECEIVABLES',
  RESIDENTIAL_REAL_ESTATE = 'RESIDENTIAL_REAL_ESTATE',
  SPECIALIZED_COMMERCIAL_REAL_ESTATE = 'SPECIALIZED_COMMERCIAL_REAL_ESTATE',
  STATE_GUARANTEE = 'STATE_GUARANTEE',
  STOCKS = 'STOCKS',
  SUBORDINATION = 'SUBORDINATION',
  VEHICLES = 'VEHICLES',
  OTHER = 'OTHER',
}

export enum LoanBookSecurityMeasureType {
  COMBINED_GUARANTEE = 'COMBINED_GUARANTEE',
  COMPANY_AND_PERSONAL_BILL_OF_EXCHANGE = 'COMPANY_AND_PERSONAL_BILL_OF_EXCHANGE',
  COMPANY_BILL_OF_EXCHANGE = 'COMPANY_BILL_OF_EXCHANGE',
  COMPANY_GUARANTEE = 'COMPANY_GUARANTEE',
  NON_NOTARIAL_PLEDGE_OF_RECEIVABLES = 'NON_NOTARIAL_PLEDGE_OF_RECEIVABLES',
  NOTARIAL_PLEDGE_OF_RECEIVABLES = 'NOTARIAL_PLEDGE_OF_RECEIVABLES',
  OTHER_BILL_OF_EXCHANGE = 'OTHER_BILL_OF_EXCHANGE',
  PERSONAL_BILL_OF_EXCHANGE = 'PERSONAL_BILL_OF_EXCHANGE',
  PERSONAL_GUARANTEE = 'PERSONAL_GUARANTEE',
  INVEGA_PG3 = 'INVEGA_PG3',
  INVEGA_PGF2 = 'INVEGA_PGF2',
  INVEGA_PGP2 = 'INVEGA_PGP2',
}

export interface LoanBookSecurityMeasureFormValues {
  securityMeasure: LoanBookSecurityMeasureOption
  securityMeasureType: LoanBookSecurityMeasureType
  amount: number
  ltvRatio: number
  collateral: number
  startDate: string
  endDate: string
  secondaryPledge: boolean
  document: ReportFileDTOInput
  companyCode?: string
  companyName?: string
  naturalPersonName?: string
  naturalPersonSurname?: string
}

export interface LoanBookSecurityMeasureDTO {
  companyCode?: string
  companyName?: string
  naturalPersonName?: string
  naturalPersonSurname?: string
  amount: number
  collateral: number
  id: string
  ltvRatio: number
  securityMeasure: LoanBookSecurityMeasureOption
  securityMeasureType: LoanBookSecurityMeasureType
  securityMeasureNumber: string
  comment: string
  startDate: string
  endDate: string
  secondaryPledge: boolean
  document: ReportFileDTOInput
}

export interface AddNewLoanBookSSecurityMeasureData {
  addLoanBookSecurityMeasure: LoanBookSecurityMeasureDTO
}

export interface AddNewLoanBookSecurityMeasureVariables extends LoanBookSecurityMeasureFormValues {
  loanBookAgreementId: string
}

export interface EditLoanBookSecurityMeasureData {
  updateLoanBookSecurityMeasure: LoanBookSecurityMeasureDTO
}

export interface EditLoanBookSecurityMeasureVariables extends LoanBookSecurityMeasureFormValues {
  id: string
}
